import React, { createContext, ReactNode, useContext, useState } from 'react'
import {
  BookableShipmentSavedSearch,
  GetBookableShipmentSavedSearchesResponse,
  useGetBookableShipmentSavedSearches,
} from '../types/apiTypes'

interface ISavedLanesState {
  savedLanes: BookableShipmentSavedSearch[] | undefined
  loading: boolean
  refetch: () => Promise<GetBookableShipmentSavedSearchesResponse | null>
  updateSavedLanesState: (savedLane: BookableShipmentSavedSearch) => void
  removeSavedLaneFromState: (savedLaneId: number) => void
}

const SavedLanesContext = createContext<ISavedLanesState>({
  savedLanes: [],
  loading: false,
  refetch: async () => Promise.resolve(null),
  updateSavedLanesState: () => {},
  removeSavedLaneFromState: () => {},
})

interface ISavedLanesProviderProps {
  children: ReactNode
}

export const SavedLanesProvider = ({ children }: ISavedLanesProviderProps) => {
  const [savedLanes, setSavedLanes] = useState<BookableShipmentSavedSearch[]>([])

  /**
   * Fetching Saved Lanes
   */
  const { loading, refetch } = useGetBookableShipmentSavedSearches({
    resolve: (data: GetBookableShipmentSavedSearchesResponse) => {
      const { bookableShipmentSavedSearches = [] } = data || {}
      const filteredSavedSearches = bookableShipmentSavedSearches.filter(
        ({ sourceType }) => sourceType === 'cm_created' || sourceType === 'dispatcher_created'
      )
      setSavedLanes(filteredSavedSearches)
      return data
    },
  })

  /**
   * Updates savedLanes state.
   * If updatedSavedLane contains an id already in state then the lane is updated,
   * however if lane is not already in state it is appended.
   *
   */
  const updateSavedLanesState = (updatedSavedLane: BookableShipmentSavedSearch) => {
    const isExisting = savedLanes.some(lane => lane.id === updatedSavedLane.id)
    if (isExisting) {
      setSavedLanes(lanes =>
        lanes.map(lane => {
          if (lane.id === updatedSavedLane.id) {
            return updatedSavedLane
          }
          return lane
        })
      )
    } else {
      setSavedLanes(savedLanes => [...savedLanes, updatedSavedLane])
    }
  }

  /**
   * Removes a saved lane with the given id from state
   */
  const removeSavedLaneFromState = (savedLaneId: number) => {
    setSavedLanes(savedLanes => savedLanes.filter(lane => lane.id !== savedLaneId))
  }

  const state = {
    savedLanes,
    loading,
    refetch,
    updateSavedLanesState,
    removeSavedLaneFromState,
  }

  return <SavedLanesContext.Provider value={state}>{children}</SavedLanesContext.Provider>
}

export const useSavedLanesContext = () => useContext(SavedLanesContext)
